// The admin's bar along the bottom of a page they edit — the bibliography, the glossary,
// a chapter. One shell for all three, and it reports how tall it is, so the
// cookie notice can sit on top of it rather than over its buttons.
import { useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from "react";

let height = 0;
const listeners = new Set<() => void>();

function setHeight(next: number): void {
  if (next === height) return;
  height = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** How much of the bottom of the screen a bar covers: 0 when there is none. */
export function useBottomBarHeight(): number {
  return useSyncExternalStore(subscribe, () => height);
}

export function BottomBar({ children }: { children: ReactNode }) {
  const bar = useRef<HTMLDivElement>(null);

  // Measured rather than assumed: on a phone the chapter's buttons wrap onto
  // a second row, and a fixed offset would leave the notice over half of them.
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.observe(el);
    setHeight(el.offsetHeight);
    return () => {
      observer.disconnect();
      setHeight(0);
    };
  }, []);

  return (
    <div ref={bar} className="fixed inset-x-0 bottom-0 z-30 border-t border-rule bg-paper/95 backdrop-blur">
      {children}
    </div>
  );
}
